import React, {useState, useRef} from "react";
import {useNavigate, useLocation} from "react-router-dom";
import BasicLayout from "../components/BasicLayout";
import bgImage from "assets/images/ai-background.jpg";
import {Card, TextField, Button, Typography, Box, Stack, IconButton} from "@mui/material";
import colors from "assets/theme/base/colors";
import api from "API/api";
import {toast} from "sonner";
import {ArrowForward, Send} from "@mui/icons-material";
import { useAuth } from "../../../context/authContext";

const VerifyEmail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {white, gradients} = colors;
  const [show, setShow] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const { user, updateUser } = useAuth();
  const [code, setCode] = useState(new Array(6).fill(""));
  const inputRefs = useRef([]);

  const handleChange = (index, event) => {
    const value = event.target.value;

    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };
  const handleKeyDown = (index, event) => {
    if (event.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };
  const getVerificationCode = () => code.join("");
  const handleVerify = async () => {
    const verificationCode = getVerificationCode();

    if (verificationCode.length < 6) {
      toast.error("Please enter all 6 digits.");
      return;
    }

    try {
      setLoading(true);
      const response = await api.post("/verify-email", {
        verification_code: verificationCode,
      });

      if (response.status === 200) {
        toast.success(response?.data?.message);
        updateUser({ is_verified: true });
        const from = location.state?.from?.pathname || "/dashboard";
        navigate(from);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Error verifying email.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    toast.info("We have sent a verification code to your email");
    try {
      const response = await api.put(`/userapi`, {new_email: email});
      updateUser(response?.data?.data);
    } catch (error) {
      toast.error(error?.response?.data?.message);
    } finally {
      setShow(false);
      setEmail("");
    }
  };

  return (
    <BasicLayout image={bgImage}>
      <Card
        sx={{
          padding: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          width: "500px",
          boxShadow: 3,
          borderRadius: 3,
        }}
      >
        <Typography
          variant="h5"
          sx={{mb: 2, fontSize: "25px", fontWeight: "bold"}}
        >
          Verify Your Email
        </Typography>
        <Typography variant="body1" sx={{mb: 3, fontSize: "16px"}}>
          We have sent a verification code to your email
        </Typography>
        <Box sx={{display: "flex", gap: 1}}>
          {code.map((num, index) => (
            <TextField
              key={index}
              value={num}
              autoComplete="off"
              onChange={(event) => handleChange(index, event)}
              onKeyDown={(event) => handleKeyDown(index, event)}
              inputRef={(el) => (inputRefs.current[index] = el)}
              inputProps={{
                maxLength: 1,
                style: {textAlign: "center", fontSize: "2rem"},
              }}
              sx={{
                width: "3rem",
                height: "3.5rem",
                textAlign: "center",
                fontSize: "2rem",
                "& .MuiInputBase-input": {
                  padding: "0.5rem",
                },
              }}
            />
          ))}
        </Box>
        <Typography
          variant="caption" sx={{my: 3, color: "info.main", cursor: "pointer", fontWeight: 500}}
          onClick={() => setShow(true)}
        >
          Didn’t get the code? Try new email.
        </Typography>
        {show &&
          <Stack direction="row" gap={1} width={1} alignItems="center" px={2} mb={2}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              autoComplete="off"
              onChange={(e) => setEmail(e.target.value)}
            />
            <IconButton
              onClick={handleSubmit}
              sx={{background: '#063863', color: '#fff', "&:hover": {background: '#063863', color: '#fff'}}}>
              <Send/>
            </IconButton>
          </Stack>
        }
        <Button
          variant="contained"
          onClick={handleVerify}
          disabled={loading}
          sx={{
            height: "38px",
            width: "157px",
            fontSize: "14px",
            fontWeight: "500",
            background: `${gradients.info.main}`,
            color: white.main,
            border: "none",
            "&:hover": {
              background: `${gradients.info.state} !important`,
              color: white.main,
            },
          }}
        >
          {loading ? "Verifying..." : "Verify"}
        </Button>
      </Card>
    </BasicLayout>
  );
};

export default VerifyEmail;
